import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Privacy-oriented "Enhancements" toggles that ape the WA Web Plus
 * "Private Mode" section. Each flag is a CSS-only privacy blur the
 * user opts into; nothing here calls into WA's Store or presence
 * layer. The companion stylesheet (`EnhancementsStyles.tsx`) mounts
 * matching rules in `document.head` that target stable WA selectors
 * and apply `filter: blur(…)` with `:hover` revealing the content.
 *
 * State persists to `localStorage` — same pattern as DisplaySettings —
 * so the user's privacy preferences survive page reload and aren't
 * written back to the user record.
 */

const STORAGE_KEY = 'bt:enhancements';

/** Canonical list of toggles surfaced in the Enhancements sub-page.
 *  Mirrors the WA Web Plus "Private Mode" section. Two categories:
 *
 *  Blur features (CSS-only, hover to reveal) — these add a
 *  `filter: blur(…)` over sensitive surfaces and ship a `:hover`
 *  unblur for the same selector:
 *    - blurContactNames     names in chats list, header, group bubbles
 *    - blurContactPhotos    avatars across all three surfaces
 *    - blurConversationMessages   message bubbles (incoming + outgoing)
 *    - blurComposerInput    the typing area
 *
 *  Visual privacy (CSS-only, no hover reveal) — these hide a state
 *  indicator on the user's own screen so a shoulder-surfer can't
 *  read it:
 *    - hideTypingStatus     "typing…" status under the contact name
 *    - hideRecordingStatus  "recording audio…" status
 *
 *  Store-level features (NOT YET IMPLEMENTED — UI toggle persists
 *  to localStorage but the WA Store patch is still TODO). These
 *  require monkey-patching the WA Store, which is risky and out of
 *  scope for the current Enhancements iteration. We surface the
 *  toggles so the UI matches the WA Web Plus reference, and add a
 *  badge in the page describing the pending state:
 *    - viewStatusesPrivately
 *    - playAudioMessagesPrivately
 *    - disableReadReceipts
 *    - hideOnlineStatus
 *
 *  To add a feature: append the id here, add a CSS rule (or Store
 *  patch) in EnhancementsStyles / WAPI, and add a translated row to
 *  EnhancementsPage. */
export const ENHANCEMENT_IDS = [
  'blurContactNames',
  'blurContactPhotos',
  'blurRecentMessages',
  'blurConversationMessages',
  'blurComposerInput',
  'hideTypingStatus',
  'hideRecordingStatus',
  'viewStatusesPrivately',
  'playAudioMessagesPrivately',
  'disableReadReceipts',
  'hideOnlineStatus',
] as const;

/** Ids that depend on a Store-level patch that hasn't been wired yet.
 *  The Enhancements page renders a "Coming soon" badge next to these
 *  rows so the user knows the toggle persists but nothing applies.
 *  viewStatusesPrivately and playAudioMessagesPrivately have the
 *  CSS-blur half implemented (via WA Plus's selectors); playAudio
 *  now also has the Store-level markPlayed suppression wired via
 *  `play-audio-privately-patch.ts`. disableReadReceipts has its own
 *  Meta-loader-based patch (`disable-read-receipts-patch.ts`) that
 *  suppresses sendConversationSeen, so it's off the "Soon" list too. */
export const ENHANCEMENT_NOT_WIRED: ReadonlyArray<(typeof ENHANCEMENT_IDS)[number]> = [];

export type EnhancementId = (typeof ENHANCEMENT_IDS)[number];

export type EnhancementsState = Record<EnhancementId, boolean>;

/** Default state — every blur is OFF. Privacy features are opt-in:
 *  flipping them on for a first-time user without warning would be
 *  confusing ("why is everything blurry?"). */
const defaults: EnhancementsState = ENHANCEMENT_IDS.reduce(
  (acc, id) => {
    acc[id] = false;
    return acc;
  },
  {} as EnhancementsState,
);

const hydrate = (): EnhancementsState => {
  try {
    const raw =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem(STORAGE_KEY)
        : null;
    if (!raw) return { ...defaults };
    const parsed = JSON.parse(raw) as Partial<EnhancementsState>;
    return { ...defaults, ...parsed };
  } catch {
    return { ...defaults };
  }
};

const persist = (state: EnhancementsState) => {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (err) {
    console.warn('[enhancements] persist failed', err);
  }
};

const enhancementsSlice = createSlice({
  name: 'Enhancements',
  initialState: hydrate(),
  reducers: {
    setEnhancement(
      state,
      action: PayloadAction<{ id: EnhancementId; enabled: boolean }>,
    ) {
      state[action.payload.id] = action.payload.enabled;
      persist(state);
    },
    toggleEnhancement(state, action: PayloadAction<EnhancementId>) {
      state[action.payload] = !state[action.payload];
      persist(state);
    },
    setAllEnhancements(state, action: PayloadAction<boolean>) {
      for (const id of ENHANCEMENT_IDS) {
        state[id] = action.payload;
      }
      persist(state);
    },
    resetEnhancements(state) {
      for (const id of ENHANCEMENT_IDS) {
        state[id] = false;
      }
      persist(state);
    },
  },
});

export const {
  setEnhancement,
  toggleEnhancement,
  setAllEnhancements,
  resetEnhancements,
} = enhancementsSlice.actions;

export default enhancementsSlice.reducer;
